import { Button, IconTile } from '@components'
import { getSettingsPageUrl } from '@constants'
import { PermissionSet, hasPermission, useBusinessContext, usePermission } from '@context'
import { useTheme } from '@hooks'
import { fetchPeople } from '@query'
import { getUserDisplayName } from '@utils'
import { getAbsoluteUrl } from '@utils/getAbsoluteUrl'
import { useQuery } from '@tanstack/react-query'
import copy from 'copy-to-clipboard'
import React, { useMemo } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaCog, FaFileInvoice, FaLink, FaPen, FaUserShield } from 'react-icons/fa'
import styled from 'styled-components'

interface Props {
  onAddDetails: () => void
  onClose: () => void
}

export const CreateInvoiceBlockedStep: React.FC<Props> = ({ onAddDetails, onClose }) => {
  const [t] = useTranslation()
  const theme = useTheme()
  const { slug } = useBusinessContext()
  const canEditSettings = usePermission(PermissionSet.INVOICING_ADMIN)
  const settingsUrl = getAbsoluteUrl(getSettingsPageUrl(slug, { page: 'invoicing-general' }))

  const { data: peopleData, isFetched } = useQuery({
    queryKey: [slug, 'people'],
    queryFn: () => fetchPeople({ slug }, { page_size: 1000 }),
    enabled: !canEditSettings
  })

  const adminNames = useMemo(() => {
    const people = peopleData?.results ?? []
    return people
      .filter(person => hasPermission(person.permissions, PermissionSet.INVOICING_ADMIN))
      .map(person => getUserDisplayName(person))
      .filter(Boolean)
      .join(', ')
  }, [peopleData])

  const copySettingsLink = () => {
    copy(settingsUrl)
    toast.success(t('components.copyValue.copied'))
  }

  let noPermissionFootnote: string | null = null
  if (isFetched) {
    noPermissionFootnote = adminNames
      ? t('invoicing.form.blockedFootnoteAdmins', { names: adminNames })
      : t('invoicing.form.blockedFootnoteNoAdmins')
  }

  const view = canEditSettings
    ? {
        title: t('invoicing.form.blockedTitleCanEdit'),
        body: t('invoicing.form.blockedDescriptionCanEdit'),
        footnote: t('invoicing.form.blockedFootnoteCanEdit'),
        footnoteIcon: <FaCog />,
        primaryIntent: 'primary' as const,
        primaryIcon: <FaPen />,
        primaryLabel: t('invoicing.form.blockedAddDetails'),
        primaryOnClick: onAddDetails,
        secondaryLabel: t('general.cancel')
      }
    : {
        title: t('invoicing.form.blockedTitleNoPermission'),
        body: t('invoicing.form.blockedDescriptionNoPermission'),
        footnote: noPermissionFootnote,
        footnoteIcon: <FaUserShield />,
        primaryIntent: undefined,
        primaryIcon: <FaLink />,
        primaryLabel: t('invoicing.form.copyLinkToSettings'),
        primaryOnClick: copySettingsLink,
        secondaryLabel: t('general.close')
      }

  return (
    <Wrapper>
      <BlockedIconTile
        color={theme.colors.neutralGray}
        iconColor={theme.colors.menuBg}
        icon={<FaFileInvoice />}
      />
      <Heading>{view.title}</Heading>
      <Body>{view.body}</Body>
      <ButtonRow>
        <Button intent={view.primaryIntent} icon={view.primaryIcon} onClick={view.primaryOnClick}>
          {view.primaryLabel}
        </Button>
        <Button onClick={onClose}>{view.secondaryLabel}</Button>
      </ButtonRow>
      {view.footnote && (
        <Footnote>
          {view.footnoteIcon}
          {view.footnote}
        </Footnote>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.xxs}rem
    ${({ theme }) => theme.spacing.xs}rem;
`

const BlockedIconTile = styled(IconTile)`
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;

  svg {
    width: ${({ theme }) => theme.fontSize.xl}rem;
    height: ${({ theme }) => theme.fontSize.xl}rem;
  }
`

const Heading = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.menuBg};
`

const Body = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.metalGray};
  text-wrap: pretty;
`

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
`

const Footnote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};

  svg {
    flex-shrink: 0;
    margin-top: 0.15rem;
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
  }
`
